<template>
    <div class="w-full max-w-4xl mx-auto p-4">
        <div class="flex items-center justify-between mb-6">
            <h5 class="text-lg font-bold text-gray-800">Points de vigilance</h5>
            <span class="text-sm text-gray-500">
                {{ tourDetails.vigilances.length }} point(s) à vérifier
            </span>
        </div>

        <!-- Message si aucune vigilance -->
        <div v-if="tourDetails.vigilances.length === 0"
             class="bg-gray-50 rounded p-4 text-center">
            <p class="text-gray-600">Aucun point de vigilance pour cette tournée</p>
        </div>

        <!-- Liste des vigilances -->
        <div v-else class="space-y-4">
            <div v-for="(vigilance, index) in tourDetails.vigilances"
                 :key="vigilance.id"
                 class="border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md"
                 :class="[acknowledgedVigilances[index] ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white']"
                 @click="toggleVigilance(index)">
                
                <div class="p-4">
                    <div class="flex items-start gap-3">
                        <!-- Checkbox -->
                        <Checkbox v-model="acknowledgedVigilances[index]"
                                :binary="true"
                                class="mt-1"
                                @click.stop />
                        
                        <div class="flex-1">
                            <!-- Points de livraison -->
                            <div v-for="delivery in vigilance.deliveries"
                                 :key="delivery.id"
                                 class="mb-3">
                                <h6 class="text-base font-bold text-gray-800 mb-1">
                                    {{ delivery.deliveryLocation.name }}
                                </h6>
                                <p class="text-sm text-gray-600">
                                    {{ delivery.deliveryLocation.address }}
                                </p>
                            </div>

                            <!-- Vigilance details -->
                            <div class="bg-orange-50 rounded p-3 mt-3">
                                <div class="flex gap-2 mb-1">
                                    <div class="flex items-center">
                                        <i class="pi pi-exclamation-circle text-orange-500 text-sm leading-none"></i>
                                    </div>
                                    <div class="flex items-center">
                                        <span class="text-sm font-semibold text-orange-800">
                                            {{ vigilance.title }}
                                        </span>
                                    </div>
                                </div>
                                <p class="text-sm text-gray-700 pl-6">
                                    {{ vigilance.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Checkbox d'engagement -->
            <div class="border rounded-lg p-4 bg-gray-50">
                <div class="flex items-start gap-3">
                    <Checkbox v-model="userCommitment"
                            :binary="true"
                            class="mt-1" />
                    <label class="text-sm text-gray-700">
                        Je m'engage à porter une attention particulière aux points de vigilance mentionnés ci-dessus pendant ma tournée.
                    </label>
                </div>
            </div>
        </div>

        <!-- Navigation -->
        <div class="flex gap-3 mt-6">
            <Button label="Retour"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    @click="$emit('prev-step')"
                    class="flex-1 p-2" />
            <Button label="Suivant"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    :disabled="!canProceed"
                    @click="handleNextStep"
                    class="flex-1 p-2" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    tourDetails: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['prev-step', 'next-step']);

const acknowledgedVigilances = ref(Array(props.tourDetails.vigilances.length).fill(false));
const userCommitment = ref(false);

const allVigilancesAcknowledged = computed(() => {
    return acknowledgedVigilances.value.every(ack => ack) || 
           props.tourDetails.vigilances.length === 0;
});

const canProceed = computed(() => {
    if (props.tourDetails.vigilances.length === 0) return true;
    return allVigilancesAcknowledged.value && userCommitment.value;
});

const toggleVigilance = (index) => {
    acknowledgedVigilances.value[index] = !acknowledgedVigilances.value[index];
};

const handleNextStep = () => {
    emit('next-step');
};
</script>